import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { PermissionEnum } from '@Application/Security/Roles';
import WithdrawInterviewConsent from '@Application/Beneficiaries/Interviews/Commands/WithdrawInterviewConsent';
import withdrawInterviewConsent from '@Application/Beneficiaries/Interviews/withdrawInterviewConsent';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import useLocalInterviewPort from '@UI/Providers/PortsProvider/Hooks/LocalInterviews/useLocalInterviewPort';
import useOutboxPort from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxPort';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button, ConfirmDeleteDialog, Message } from '@verclam/ui-kit';

const PURGE_FAILURE_MESSAGE =
	"Le consentement a bien été retiré et l'enregistrement supprimé sur le serveur, mais sa copie " +
	"locale n'a pas pu être effacée sur cet appareil. Rechargez la page ou réessayez plus tard " +
	'depuis le dossier du bénéficiaire pour terminer sa suppression.';

type Props = {
	readonly facilityId: number;
	readonly subject: SubjectAddress;
	readonly interviewId: number;
	/** Already formatted `JJ/MM/AAAA` — the `confirmText` this dialog asks back. */
	readonly conductedOnDisplay: string;
};

/**
 * The single destructive action of the consultation screen (T7): withdrawing a granted consent.
 * `ConfirmDeleteDialog`, not `ConfirmDialog` — this deletes the audio without any way back — with
 * the interview's own date as `confirmText`, a real business value already on screen (two interviews
 * on the same day are not disambiguated by it: the resource acted upon is always the route, this
 * reentry is an attention device on an irreversible act, not a disambiguator).
 *
 * `acceptCallback` dispatches the withdrawal, then closes the dialog synchronously — the component
 * never closes itself, and waiting for the call to settle would leave it open long enough for a
 * second click. The server is the authority: the local purge only starts after the `DELETE` actually
 * succeeds, never before, never after a failure.
 *
 * The local match is read through `LocalInterviewPort`, never `item.context.interviewId` — V-19
 * stopped writing that key into the queue item's context, so a match on it would always render `[]`,
 * `Promise.all([])` would resolve, and this dialog would keep promising an erasure "on the server as
 * on this device" that never actually happened, with nothing turning red anywhere. A local interview
 * whose `interviewId` equals the one just withdrawn is discarded from the queue first (when it still
 * carries an `outboxItemId` — a capture never carried through has none), then removed from the local
 * store itself: skipping the second step would leave that local interview on this device forever,
 * participants in clear text included, since an item leaving the queue is no longer a reconciliation
 * trigger. A purge failure is not swallowed by `ignoreHandledError` (reserved here for the
 * withdrawal's own rejection, already surfaced by `InterviewsNotifications`) — it is the only signal
 * that bytes are still on this device, so it renders on the screen, past the point where the dialog
 * has already closed.
 */
const InterviewConsentConfirmationDialogs: React.FC<Props> = ({
	facilityId,
	subject,
	interviewId,
	conductedOnDisplay,
}) => {
	const dispatch = useDispatch();
	const outboxPort = useOutboxPort();
	const localInterviewPort = useLocalInterviewPort();

	const [isDialogVisible, setIsDialogVisible] = React.useState(false);
	const [isWithdrawing, setIsWithdrawing] = React.useState(false);
	const [purgeErrorMessage, setPurgeErrorMessage] = React.useState<string | null>(null);

	const handleOpenDialog = React.useCallback(() => {
		setPurgeErrorMessage(null);
		setIsDialogVisible(true);
	}, []);

	const handleDialogHide = React.useCallback(() => {
		setIsDialogVisible(false);
	}, []);

	const handleAccept = React.useCallback(() => {
		setIsWithdrawing(true);

		const withdraw = async (): Promise<void> => {
			await dispatch(
				withdrawInterviewConsent(new WithdrawInterviewConsent(facilityId, subject, interviewId)),
			);

			const matchingLocalInterviews = localInterviewPort
				.snapshot()
				.filter((localInterview) => localInterview.interviewId === interviewId);

			try {
				await Promise.all(
					matchingLocalInterviews.map(async (localInterview) => {
						if (localInterview.outboxItemId !== null) {
							await outboxPort.discard(localInterview.outboxItemId);
						}

						await localInterviewPort.remove(localInterview.id);
					}),
				);
			} catch {
				setPurgeErrorMessage(PURGE_FAILURE_MESSAGE);
			}
		};

		withdraw()
			.catch(ignoreHandledError)
			.finally(() => setIsWithdrawing(false));

		// Closes immediately regardless of the outcome above: waiting for the call to settle would
		// leave the dialog open long enough for a second click. `acceptCallback` never triggers
		// `onHide` on `ConfirmDeleteDialog` — this is the only place that closes it.
		setIsDialogVisible(false);
	}, [dispatch, facilityId, subject, interviewId, outboxPort, localInterviewPort]);

	return (
		<React.Fragment>
			<WithPermission permission={PermissionEnum.INTERVIEW_WRITE}>
				<Button
					type="button"
					severity="danger"
					label="Retirer le consentement"
					loading={isWithdrawing}
					onClick={handleOpenDialog}
				/>
			</WithPermission>
			{purgeErrorMessage !== null && <Message severity="warn" text={purgeErrorMessage} />}
			<ConfirmDeleteDialog
				visible={isDialogVisible}
				onHide={handleDialogHide}
				header="Retirer le consentement"
				message={`Cette action est irréversible. L'enregistrement audio de l'entretien du ${conductedOnDisplay} sera définitivement supprimé, sur le serveur comme sur cet appareil. Saisissez la date de l'entretien pour confirmer.`}
				confirmText={conductedOnDisplay}
				acceptCallback={handleAccept}
			/>
		</React.Fragment>
	);
};

export default InterviewConsentConfirmationDialogs;
